<script setup lang="ts">
import dayjs, { type Dayjs } from 'dayjs'
import { ref, watch } from 'vue'
import { $t, usePrevioCore } from '@previo/core'
import { type IErrorState } from './model/ErrorState'
import Segment from './components/Segment.vue'

type Emits = {
  (e: 'next'): void
  (e: 'prev'): void
}

const props = defineProps({
  defaultTime: {
    type: String,
    validator: (value?: string): boolean => {
      if (!value) return true
      return dayjs(value, 'HH:mm', true).isValid()
    },
    required: false,
  },
})

const model = defineModel<Dayjs | null>()

const emits = defineEmits<Emits>()

const { UUID } = usePrevioCore()

const id = UUID.v7()

const segmentComponent = ref<InstanceType<typeof Segment>>()

const text = ref<string>('DD')
const buffer = ref<string>('') // holds temporary typed digits

const isValid = (): boolean => {
  return model.value?.tz().isValid() === true && text.value !== 'DD'
}

const validate = (errorState: IErrorState): void => {
  if (isValid()) {
    return
  }

  errorState.set($t('The value {value} is required', { value: 'DD' }))
}

const focus = (): void => {
  segmentComponent.value?.focus()
}

const ensureValid = (): void => {
  const raw = buffer.value.trim()

  if (!/^\d+$/.test(raw)) {
    return
  }

  const day = parseInt(raw, 10)

  if (day < 1 && day > 31) {
    return
  }

  if (!model.value) {
    return
  }

  model.value = model.value.tz().date(day)

  emits('next')
}

const onInput = (value: string): void => {
  buffer.value = (buffer.value + value).slice(-2) // max 2 digits

  const inputDay = parseInt(buffer.value, 10)

  if (buffer.value.length === 2) {
    const base =
      model.value ?? (props.defaultTime ? dayjs(props.defaultTime, 'HH:mm') : dayjs().tz())

    const maxValidDay = base.daysInMonth()
    const safeDay = Math.min(Math.max(inputDay, 1), maxValidDay)

    model.value = base.date(safeDay)

    emits('next')
    buffer.value = ''
  } else {
    text.value = buffer.value
  }
}

const onDelete = (): void => {
  buffer.value = ''
  text.value = 'DD'
}

const onAdd = (): void => {
  model.value = model.value?.tz().add(1, 'day')
}

const onSubtract = (): void => {
  model.value = model.value?.tz().subtract(1, 'day')
}

const onNext = (): void => {
  ensureValid()
  emits('next')
}

const onPrev = (): void => {
  ensureValid()
  emits('prev')
}

const onConfirm = ensureValid

watch(
  model,
  () => {
    if (!model.value) {
      text.value = 'DD'
      return
    }

    text.value = model.value.tz().format('DD')
  },
  { immediate: true },
)

defineExpose({
  focus,
  validate,
})
</script>

<template>
  <segment
    :id="id"
    ref="segmentComponent"
    :text="text"
    @input="onInput"
    @delete="onDelete"
    @next="onNext"
    @prev="onPrev"
    @add="onAdd"
    @subtract="onSubtract"
    @confirm="onConfirm"
  />
</template>
